import { ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import { DataProp } from 'editorjs-blocks-react-renderer';
import QUESTION_TUTORIALS_QUERY from '@/graphql/queries/question-tutorials.graphql';
import VideoPlayer from '@/components/VideoPlayer';
import BlocksRenderer from '@/components/BlocksRenderer.tsx';
import Button from '@/components/Button.tsx';
import DownloadPdfButton, { Attachment } from '@/components/DownloadPdfButton.tsx';
import { Spinner } from '@/components/Loader.tsx';
import { Lock } from '@/components/Icons.tsx';
import { TypoCallout, TypoCaption, TypoFootnote } from '@/components/Typography.tsx';
import useVideoProgress, { ClientVideo } from '@/hooks/useVideoProgress.ts';
import { toJSON } from '@/utils/helpers.tsx';

const UPGRADE_PATH = '/packages';
// SATPrac COMPLETE and CONCIERGE; the ids are the same on staging and production.
const PREMIUM_PRODUCT_IDS = new Set([70, 155]);
const EMPTY_SECTION_SUBTITLE = 'Check back soon! New walkthroughs are added to the library regularly.';

interface TutorialContentProps {
  examQuestionId: number;
}

const TutorialContent = ({ examQuestionId }: TutorialContentProps) => {
  const { data, loading, error } = useQuery<QueryResult>(QUESTION_TUTORIALS_QUERY, {
    variables: { examQuestionId },
  });

  if (loading) {
    return (
      <div className="flex justify-center py-10 text-neutral-text-soft">
        <Spinner className="w-8" />
      </div>
    );
  }

  if (error || !data) {
    return <EmptyBox title="Tutorials could not be loaded right now." subtitle="Please try again later." />;
  }

  const { explanationVideo, tutorials } = data.examQuestion;
  // The API has no viewer-level plan flag: owning COMPLETE / CONCIERGE counts, and so does any
  // unlocked tutorial here (access granted by some other product).
  const isPremium = data.purchasedProducts.some(p => PREMIUM_PRODUCT_IDS.has(p.productId))
    || tutorials.some(({ content, questionBank }) => [...content, ...questionBank].some(item => item.hasAccess));

  if (isPremium && !explanationVideo && !tutorials.length) {
    return <EmptyBox title="Tutorials for this question are coming soon." />;
  }

  return (
    <div className="flex flex-col gap-6">
      {explanationVideo && <Walkthrough {...explanationVideo} isFree={!isPremium} />}
      {!isPremium && <UpgradeCard />}
      {tutorials.length > 0 && <QuestionTypeTabs tutorials={tutorials} />}
    </div>
  );
};

const Walkthrough = ({ stream, image, isFree }: ExplanationVideo & { isFree: boolean }) => (
  <VideoCard
    label={isFree ? <Pill variant="free">✓ Free Walkthrough</Pill> : null}
    title="Question Breakdown"
  >
    <VideoFrame stream={stream} image={image} />
  </VideoCard>
);

const UpgradeCard = () => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-primary bg-shade-5 px-4 py-5 text-center">
      <TypoCallout className="text-neutral-text">
        SATPrac <strong>COMPLETE</strong> and <strong>CONCIERGE</strong> users have access
        to <strong>100+ hours of video tutorials</strong> that systematically break down each question type so
        that you can win on game day!
      </TypoCallout>
      <Button className="w-full max-w-52" onClick={() => navigate(UPGRADE_PATH)}>Upgrade</Button>
      <TypoFootnote className="text-neutral-text-soft">
        Zoom tours of the SATPrac site are available if you have questions for Idiot Tutor Mark Anestis.
        He can show you why and how this site will turn you into a <strong>master of the digital SAT</strong>.
      </TypoFootnote>
    </div>
  );
};

const QuestionTypeTabs = ({ tutorials }: { tutorials: QuestionTutorial[] }) => (
  <Tab.Group>
    <Tab.List className="flex justify-center gap-8 border-b border-shade-10 px-3">
      {tutorials.map(({ questionCategoryId, name }) => (
        <Tab
          key={questionCategoryId}
          className="relative -mb-px border-b-2 border-[transparent] pb-2.5 pt-1 outline-0
                     font-semibold text-callout uppercase tracking-wide text-neutral-text-softer
                     hover:text-neutral-text
                     ui-selected:border-neutral-text-strong ui-selected:text-neutral-text-strong
                     m:min-h-11"
        >
          {name}
        </Tab>
      ))}
    </Tab.List>
    <Tab.Panels className="mt-6">
      {tutorials.map(({ questionCategoryId, content, questionBank }) => (
        <Tab.Panel key={questionCategoryId} className="flex flex-col gap-8">
          <TutorialSection
            title={`Content Tutorials (${content.length} ${content.length === 1 ? 'video' : 'videos'})`}
            items={content}
            emptyText="There are no CONTENT tutorials at this time for this question type."
            scrollable
          />
          <TutorialSection
            title="Question Bank Series"
            items={questionBank}
            emptyText="There are no QUESTION BANK tutorials at this time for this question type."
          />
        </Tab.Panel>
      ))}
    </Tab.Panels>
  </Tab.Group>
);

interface TutorialSectionProps {
  title: string;
  items: TutorialVideo[];
  emptyText: string;
  scrollable?: boolean;
}

const TutorialSection = ({ title, items, emptyText, scrollable }: TutorialSectionProps) => {
  const isLocked = items.some(item => !item.hasAccess);
  const isSeries = items.length > 1;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <TypoFootnote className="font-extraBold uppercase tracking-wide text-neutral-text-strong">{title}</TypoFootnote>
        {isLocked && <Pill variant="locked">🔒 Complete & Concierge</Pill>}
      </div>
      {
        items.length
          ? (
            // The content list can run to 10+ videos, so it scrolls inside its own box
            // instead of pushing the question bank series far down the tab.
            // `relative` makes the box the containing block for absolutely positioned content
            // inside the players (Chrome's native HLS <video>). With no positioned ancestor up
            // to the page, that content escaped the box's clipping and stretched the page.
            <div className={`flex flex-col gap-4 ${scrollable ? 'relative max-h-100 overflow-y-auto pr-1' : ''}`}>
              {items.map(({ hasAccess, video }, index) => (
                <TutorialVideoCard
                  key={video.videoId}
                  part={isSeries ? index + 1 : undefined}
                  hasAccess={hasAccess}
                  video={video}
                />
              ))}
            </div>
          )
          : <EmptyBox title={emptyText} subtitle={EMPTY_SECTION_SUBTITLE} />
      }
    </section>
  );
};

interface TutorialVideoCardProps extends TutorialVideo {
  part?: number;
}

const TutorialVideoCard = ({ part, hasAccess, video }: TutorialVideoCardProps) => {
  const { videoId, name, description, image, stream, subtitles, attachments, clientVideo } = video;
  const descriptionData = useMemo<DataProp | null>(() => toJSON(description ?? '') || null, [description]);
  const label = part
    ? <TypoCaption className="font-bold uppercase text-neutral-text-soft">Part {part}</TypoCaption>
    : null;

  return (
    <VideoCard label={label} title={name}>
      {
        hasAccess
          ? (
            <TrackedVideoFrame
              videoId={videoId}
              stream={stream}
              image={image}
              subtitles={subtitles}
              clientVideo={clientVideo}
            />
          )
          : <LockedVideoFrame image={image} />
      }
      {hasAccess && attachments.length > 0 && (
        <div className="grid grid-cols-2 gap-3 m:grid-cols-1">
          {attachments.map(file => <DownloadPdfButton key={file.fileId} file={file} />)}
        </div>
      )}
      {descriptionData?.blocks?.length ? <Description data={descriptionData} /> : null}
    </VideoCard>
  );
};

interface VideoCardProps {
  label?: ReactNode;
  title: string;
  children: ReactNode;
}

const VideoCard = ({ label, title, children }: VideoCardProps) => (
  <article className="flex flex-col gap-3 rounded-lg border border-shade-10 bg-shade-5 p-3">
    {label && <div className="flex items-center justify-between">{label}</div>}
    <TypoCallout className="font-semibold text-neutral-text-strong">{title}</TypoCallout>
    {children}
  </article>
);

interface VideoFrameProps {
  stream: string | null;
  image: string | null;
  subtitles?: string | null;
  onTimeUpdate?: ReturnType<typeof useVideoProgress>['onTimeUpdate'];
  onPlay?: () => void;
}

// `stream` stays null while MediaConvert is still encoding, so the poster stands in until it lands.
const VideoFrame = ({ stream, image, subtitles, onTimeUpdate, onPlay }: VideoFrameProps) => {
  if (!stream) {
    return (
      <Thumbnail image={image}>
        <TypoFootnote className="font-semibold text-common-white">
          This video is processing. Check back soon!
        </TypoFootnote>
      </Thumbnail>
    );
  }

  return (
    <VideoPlayer
      className="w-full"
      src={stream}
      poster={image}
      subtitles={subtitles ? { src: subtitles, label: 'English (US)', srclang: 'en-US' } : undefined}
      onTimeUpdate={onTimeUpdate}
      onPlay={onPlay}
    />
  );
};

interface TrackedVideoFrameProps extends Omit<VideoFrameProps, 'onTimeUpdate' | 'onPlay'> {
  videoId: number;
  clientVideo: ClientVideo;
}

const TrackedVideoFrame = ({ videoId, clientVideo, ...props }: TrackedVideoFrameProps) => {
  const { onTimeUpdate, registerOpen } = useVideoProgress(videoId, clientVideo);
  const opened = useRef(false);

  // Count a view on the first play rather than on mount: the tab lists every
  // tutorial for the question type, and most of them are never started.
  const onPlay = useCallback(() => {
    if (opened.current) return;

    opened.current = true;
    registerOpen();
  }, [registerOpen]);

  return <VideoFrame {...props} onTimeUpdate={onTimeUpdate} onPlay={onPlay} />;
};

const LockedVideoFrame = ({ image }: { image: string | null }) => {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      className="block w-full text-left"
      aria-label="Locked tutorial. Upgrade to SATPrac COMPLETE or CONCIERGE to watch it."
      onClick={() => navigate(UPGRADE_PATH)}
    >
      <Thumbnail image={image}>
        <span
          className="flex h-9 w-9 items-center justify-center rounded-full border border-primary bg-common-black/40"
        >
          <Lock className="h-4 w-4 text-common-white" />
        </span>
        <TypoCaption className="font-bold uppercase tracking-wide text-common-white">
          Unlock with Complete / Concierge
        </TypoCaption>
      </Thumbnail>
    </button>
  );
};

const Thumbnail = ({ image, children }: { image: string | null; children: ReactNode }) => (
  <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-common-black">
    {image && <img src={image} alt="" className="h-full w-full object-cover" />}
    <div
      className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-common-black/60 px-4 text-center"
    >
      {children}
    </div>
  </div>
);

const Description = ({ data }: { data: DataProp }) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (el && !expanded) setClamped(el.scrollHeight > el.clientHeight);
  }, [data, expanded]);

  return (
    <div className="flex flex-col items-start text-neutral-text-soft">
      <div ref={textRef} className={expanded ? '' : 'line-clamp-3'}>
        <BlocksRenderer data={data} config={{ paragraph: { className: 'text-footnote font-primary my-1' } }} />
      </div>
      {(clamped || expanded) && (
        <button
          type="button"
          className="text-caption font-semibold text-primary hover:underline"
          onClick={() => setExpanded(value => !value)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

const Pill = ({ variant, children }: { variant: 'free' | 'locked'; children: ReactNode }) => {
  const color = variant === 'free' ? 'text-success-neutral-text-softer' : 'text-primary';

  return (
    <TypoCaption className={`shrink-0 rounded-xs border border-current px-2 py-0.5 font-extraBold uppercase ${color}`}>
      {children}
    </TypoCaption>
  );
};

const EmptyBox = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <div
    className="flex flex-col items-center gap-1.5 rounded-md border border-dashed border-shade-20 px-4 py-5 text-center"
  >
    <TypoCallout className="font-medium text-neutral-text">{title}</TypoCallout>
    {subtitle && <TypoFootnote className="text-neutral-text-softer">{subtitle}</TypoFootnote>}
  </div>
);

type ExplanationVideo = {
  stream: string | null;
  image: string | null;
};

type TutorialVideo = {
  hasAccess: boolean;
  video: {
    videoId: number;
    name: string;
    description: string | null;
    image: string | null;
    stream: string | null;
    subtitles: string | null;
    attachments: Attachment[];
    clientVideo: ClientVideo;
  };
};

type QuestionTutorial = {
  questionCategoryId: number;
  name: string;
  content: TutorialVideo[];
  questionBank: TutorialVideo[];
};

interface QueryResult {
  purchasedProducts: Array<{ productId: number }>;
  examQuestion: {
    examQuestionId: number;
    explanationVideo: ExplanationVideo | null;
    tutorials: QuestionTutorial[];
  };
}

export default TutorialContent;
